import React, { forwardRef, type ForwardedRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Button as PloneButton } from '@plone/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import { clsx } from 'clsx';
import './Button.scss';

export interface ESSButtonProps {
  /** Button variant */
  variant?: 'positive' | 'negative' | 'accent' | 'link-icon';
  /** Button text (optional) */
  text?: string;
  /** Icon to display (optional) */
  icon?: React.ReactNode;
  /** Border radius degree (uses token value, e.g., 0 for none) */
  borderRadius?: string | number;
  /** Background color (overrides variant default) */
  backgroundColor?: string;
  /** Button size */
  size?: 'sm' | 'md';
  /** Whether the button should take full width */
  fullWidth?: boolean;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Icon to display before the button text (legacy support) */
  startIcon?: React.ReactNode;
  /** Icon to display after the button text (legacy support) */
  endIcon?: React.ReactNode;
  /** Whether the button is disabled */
  isDisabled?: boolean;
  /** Whether the button is disabled (alias for isDisabled) */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Button children (legacy support) */
  children?: React.ReactNode;
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** If set, clicking navigates to this URL via SPA routing (no page reload) */
  href?: string;
  /** Allow any other props from @plone/components Button */
  [key: string]: any;
}

export const Button = forwardRef<HTMLButtonElement, ESSButtonProps>(
  function Button(
    {
      variant = 'positive',
      text,
      icon,
      borderRadius,
      backgroundColor,
      size = 'md',
      fullWidth = false,
      loading = false,
      startIcon,
      endIcon,
      children,
      className,
      isDisabled,
      disabled,
      href,
      onClick,
      ...props
    },
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    const history = useHistory();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (href) {
        event.preventDefault();
        event.stopPropagation();
        history.push(flattenToAppURL(href));
      }
      onClick?.(event);
    };

    // Determine if button has text content
    const hasText = !!(text || children);
    // Determine which icon to use (new 'icon' prop or legacy startIcon/endIcon)
    const displayIcon = icon || startIcon || endIcon;
    const buttonClasses = clsx(
      'ess-button',
      `ess-button--${variant}`,
      `ess-button--${size}`,
      {
        'ess-button--full-width': fullWidth,
        'ess-button--loading': loading,
        'ess-button--disabled': isDisabled || disabled || loading,
        'ess-button--icon-only': !hasText && displayIcon,
      },
      className,
    );

    // Build inline styles for custom colors and border radius
    const customStyles: React.CSSProperties = {};
    if (backgroundColor) customStyles.backgroundColor = backgroundColor;
    if (borderRadius !== undefined) {
      customStyles.borderRadius = typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;
    }
    else {
      if (size === 'sm') { customStyles.borderRadius = "var(--essbutton-variantpositive-sm-statedefault-radius)"; }
      else if (size === 'md') { customStyles.borderRadius = "var(--essbutton-variantpositive-md-statedefault-radius)"; }
    }

    customStyles.textAlign = variant === "link-icon" ? "left" : "center";

    return (
      <PloneButton
        ref={ref}
        className={buttonClasses}
        style={customStyles}
        isDisabled={isDisabled || disabled || loading}
        onClick={handleClick as React.MouseEventHandler}
        {...props}
      >
        {loading && (
          <span className="ess-button__spinner" aria-hidden="true">
            <svg
              className="ess-button__spinner-icon"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="31.416"
                strokeDashoffset="31.416"
              >
                <animate
                  attributeName="stroke-dasharray"
                  dur="2s"
                  values="0 31.416;15.708 15.708;0 31.416"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-dashoffset"
                  dur="2s"
                  values="0;-15.708;-31.416"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </span>
        )}

        {displayIcon && !loading && (
          <span className="ess-button__icon" aria-hidden="true">
            {displayIcon}
          </span>
        )}

        {hasText && (
          <span className="ess-button__content">{text || children}</span>
        )}
      </PloneButton>
    );
  },
);

Button.displayName = 'ESSButton';
